import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface CountdownDigitProps {
    // Two-digit zero-padded string, e.g. "07".
    value: string
    className?: string
}

// Renders each character on its own so only the digits that change animate.
// The animation is a clean vertical roll, never a 360 flip.
const CountdownDigit: FC<CountdownDigitProps> = ({ value, className }): ReactNode => {
    return (
        <div className={cn('flex items-baseline justify-center', className)}>
            {value.split('').map((char, idx) => (
                <DigitCell key={`pos-${idx}`} char={char} />
            ))}
        </div>
    )
}

interface DigitCellProps {
    char: string
}

const DigitCell: FC<DigitCellProps> = ({ char }): ReactNode => {
    const reduce = useReducedMotion()

    return (
        <span className='relative inline-block h-[1em] w-[0.6em] overflow-hidden align-baseline'>
            <AnimatePresence initial={false} mode='popLayout'>
                <motion.span
                    key={char}
                    initial={reduce ? { opacity: 0 } : { y: '-100%', opacity: 0 }}
                    animate={reduce ? { opacity: 1 } : { y: 0, opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { y: '100%', opacity: 0 }}
                    transition={{ duration: reduce ? 0.15 : 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className='digit-track absolute inset-0 flex items-baseline justify-center tabular-nums'
                >
                    {char}
                </motion.span>
            </AnimatePresence>
        </span>
    )
}

export default CountdownDigit
